import React, { useState, useEffect, useRef } from 'react';
import { fetchArDashboardData } from '../../services/api';
import { DataState } from "./../../context/DataProvider";
import { IoIosArrowUp, IoIosArrowDown, IoIosRemove, IoIosWarning } from 'react-icons/io';
import toast from 'react-hot-toast';
import ArReportDocument from './ArReportDocument';

const TIME_FILTER_OPTIONS = [
    { label: "All Time", value: "all" },
    { label: "Last 10 Days", value: "10" },
    { label: "Last 30 Days", value: "30" },
    { label: "Last 60 Days", value: "60" },
    { label: "Custom Range", value: "custom" },
];

// --- Helper Functions ---
const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD
};

const getDateRange = (filterValue) => {
    const endDate = new Date();
    let startDate = new Date();

    // "all" means no date filtering
    if (filterValue === "all") {
        return { startDate: null, endDate: null };
    }

    if (filterValue === "custom") {
        return { startDate: null, endDate: null };
    }

    const days = parseInt(filterValue, 10);
    startDate.setDate(endDate.getDate() - days);

    return {
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate)
    };
};

const getProgressBarColor = (label) => {
    const l = label.toLowerCase();
    if (l.includes('91+')) return "bg-red-500";
    if (l.includes('61-90')) return "bg-red-400";
    if (l.includes('46-60')) return "bg-orange-600";
    if (l.includes('31-45')) return "bg-orange-500";
    if (l.includes('16-30')) return "bg-yellow-600";
    if (l.includes('11-15')) return "bg-yellow-500";
    if (l.includes('1-10')) return "bg-yellow-400";
    return "bg-green-500";
};

const getTrustScoreStyles = (score) => {
    if (score > 85) return "bg-green-900/30 text-green-300 border border-green-900/50";
    if (score > 65) return "bg-yellow-900/30 text-yellow-300 border border-yellow-900/50";
    return "bg-red-900/30 text-red-300 border border-red-900/50";
};

const AripDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [showReport, setShowReport] = useState(false);

    // Race condition guards
    const requestIdRef = useRef(0);  // increments on every loadDashboard call; only latest can setData
    const inFlightRef = useRef({});  // key → Promise; deduplicates concurrent fetches for same combo

    // Time filter state
    const [selectedFilter, setSelectedFilter] = useState("30");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Filter type state (created, due, both)
    const [filterType, setFilterType] = useState("created");
    const [isFilterTypeOpen, setIsFilterTypeOpen] = useState(false);

    const FILTER_TYPE_OPTIONS = [
        { label: "By Created Date", value: "created" },
        { label: "By Due Date", value: "due" },
        { label: "By Both", value: "both" },
        { label: "By Either", value: "either" },
    ];

    // Sorting state: { column: string | null, direction: 'asc' | 'desc' | null }
    const [sortConfig, setSortConfig] = useState({ column: null, direction: null });

    // Column definitions for the customer table
    const CUSTOMER_COLUMNS = [
        { key: "customer", label: "Customer", align: "left" },
        { key: "outstanding", label: "Outstanding", align: "right" },
        { key: "trustScore", label: "Trust Score", align: "center" },
        { key: "terms", label: "Terms", align: "center" },
        { key: "dueDate", label: "Due Date", align: "right" },
        { key: "status", label: "Status", align: "center" },
        { key: "lastInvoiceDate", label: "Last Invoice", align: "right" },
    ];

    // Handle column header click - cycles: asc → desc → null → asc
    const handleSort = (columnKey) => {
        setSortConfig((prev) => {
            if (prev.column !== columnKey) {
                return { column: columnKey, direction: "asc" };
            }
            if (prev.direction === "asc") {
                return { column: columnKey, direction: "desc" };
            }
            if (prev.direction === "desc") {
                return { column: null, direction: null };  // Reset to unsorted
            }
            return { column: columnKey, direction: "asc" };
        });
    };

    // Get sort indicator arrow (React Icons)
    const getSortIndicator = (columnKey) => {
        if (sortConfig.column !== columnKey) {
            return <IoIosRemove className="ml-1 text-zinc-600" size={14} />;
        }
        if (sortConfig.direction === "asc") {
            return <IoIosArrowUp className="ml-1 text-blue-400" size={14} />;
        }
        return <IoIosArrowDown className="ml-1 text-blue-400" size={14} />;
    };

    // Sort customers based on sortConfig
    const getSortedCustomers = (customerList) => {
        if (!sortConfig.column || !sortConfig.direction) {
            return customerList;
        }

        return [...customerList].sort((a, b) => {
            let aVal = a[sortConfig.column];
            let bVal = b[sortConfig.column];

            // Handle null/undefined values
            if (aVal == null) aVal = "";
            if (bVal == null) bVal = "";

            // Numeric comparison for numbers
            if (typeof aVal === "number" && typeof bVal === "number") {
                return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
            }

            // String comparison
            const comparison = String(aVal).localeCompare(String(bVal));
            return sortConfig.direction === "asc" ? comparison : -comparison;
        });
    };

    // Get token from global context
    const { userGoogleId: accessToken } = DataState();

    // Load dashboard data
    const CACHE_INDEX_KEY = "ar_dashboard_cache_index";
    const MAX_CACHE_SIZE = 25;
    const CACHE_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

    const updateCache = (key, data) => {
        try {
            // Wrap data with a timestamp for TTL checks
            const entry = { data, ts: Date.now() };
            localStorage.setItem(key, JSON.stringify(entry));

            // Update Index (LRU)
            let index = [];
            const indexStr = localStorage.getItem(CACHE_INDEX_KEY);
            if (indexStr) index = JSON.parse(indexStr);

            index = index.filter(k => k !== key); // remove if exists (move to front)
            index.unshift(key);                   // add to front

            // Prune oldest entries if over limit
            while (index.length > MAX_CACHE_SIZE) {
                const toRemove = index.pop();
                localStorage.removeItem(toRemove);
            }

            localStorage.setItem(CACHE_INDEX_KEY, JSON.stringify(index));
        } catch (e) {
            console.warn("Cache update failed", e);
        }
    };

    /** Read a cache entry. Returns parsed data if valid and not expired, null otherwise. */
    const readCache = (key) => {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return null;
            const entry = JSON.parse(raw);
            // Support both old (plain object) and new ({ data, ts }) format
            if (!entry.ts) return null; // no timestamp = treat as expired
            if (Date.now() - entry.ts > CACHE_TTL_MS) {
                // Expired — remove it
                localStorage.removeItem(key);
                return null;
            }
            return entry.data;
        } catch (e) {
            return null;
        }
    };

    const loadDashboard = async (startDate, endDate, type) => {
        const cacheKey = `ar_dashboard_${startDate}_${endDate}_${type}`;

        // Claim this request slot — any earlier in-flight loadDashboard becomes stale
        const myRequestId = ++requestIdRef.current;
        const isCurrent = () => requestIdRef.current === myRequestId;

        // 1. Cache-first with TTL check
        const cachedData = readCache(cacheKey);
        if (cachedData) {
            if (isCurrent()) {
                setData(cachedData);
                setLoading(false);
            }
            return; // valid cache hit — no API call
        }

        // 2. No cache (or expired) — fetch from API
        // Deduplicate: if prefetch is already fetching this key, reuse its Promise
        if (!inFlightRef.current[cacheKey]) {
            inFlightRef.current[cacheKey] = fetchArDashboardData(accessToken, startDate, endDate, type)
                .finally(() => { delete inFlightRef.current[cacheKey]; });
        }

        if (isCurrent()) setLoading(true);
        try {
            const result = await inFlightRef.current[cacheKey];
            if (!isCurrent()) return; // stale — a newer filter was selected, discard
            setData(result);
            updateCache(cacheKey, result);
            setError(null);
        } catch (err) {
            if (!isCurrent()) return;
            console.error("Dashboard Load Error:", err);
            setError(err.message || "Failed to load QuickBooks data");
        } finally {
            if (isCurrent()) setLoading(false);
        }
    };

    /**
     * Silently prefetch the most common filter combos in the background.
     * Only fetches combos not already in cache. Never touches UI state.
     * Runs after a delay so primary load completes first.
     */
    const prefetchCommonCombos = (skipKey) => {
        const PREFETCH_COMBOS = [
            { filter: "all", type: "created" },
            { filter: "30",  type: "created" },
            { filter: "10",  type: "created" },
            { filter: "60",  type: "created" },
            { filter: "all", type: "due" },
            { filter: "30",  type: "due" },
            { filter: "10",  type: "due" },
            { filter: "60",  type: "due" },
        ];

        setTimeout(async () => {
            // Filter: skip already cached, skip primary key, skip anything loadDashboard already fetching
            const toFetch = PREFETCH_COMBOS.map(combo => {
                const { startDate, endDate } = getDateRange(combo.filter);
                return { ...combo, startDate, endDate, key: `ar_dashboard_${startDate}_${endDate}_${combo.type}` };
            }).filter(c => c.key !== skipKey && !readCache(c.key) && !inFlightRef.current[c.key]);

            if (toFetch.length === 0) return;
            console.debug(`[Prefetch] Firing ${toFetch.length} requests sequentially`);

            for (const c of toFetch) {
                // Double check if it got covered in the meantime
                if (inFlightRef.current[c.key] || readCache(c.key)) continue;

                try {
                    const promise = fetchArDashboardData(accessToken, c.startDate, c.endDate, c.type);
                    inFlightRef.current[c.key] = promise;
                    
                    const result = await promise;
                    updateCache(c.key, result);
                    console.debug(`[Prefetch] ✓ ${c.filter}/${c.type}`);
                } catch (e) {
                    console.debug(`[Prefetch] ✗ ${c.filter}/${c.type}:`, e.message);
                } finally {
                    delete inFlightRef.current[c.key];
                }
                
                // Add a small delay between requests to avoid rate limits
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }, 1500);
    };




    const handleSync = async () => {
        const { startDate, endDate } = getDateRange(selectedFilter);
        const s = selectedFilter === "custom" ? customStartDate : startDate;
        const e = selectedFilter === "custom" ? customEndDate : endDate;
        if (selectedFilter === "custom" && (!s || !e)) {
            toast.error("Please set both start and end dates first.");
            return;
        }
        const cacheKey = `ar_dashboard_${s}_${e}_${filterType}`;
        setIsRefreshing(true);
        try {
            const result = await fetchArDashboardData(accessToken, s, e, filterType);
            setData(result);
            updateCache(cacheKey, result);
            setError(null);
            toast.success("Dashboard synced with QuickBooks.");
        } catch (err) {
            console.error("Sync Error:", err);
            toast.error("Sync failed. Please try again.");
        } finally {
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        if (!accessToken) {
            setLoading(false);
            return;
        }
        // Fires on mount AND whenever filters change.
        // loadDashboard is cache-first: if data exists for this combo it shows
        // instantly with NO API call. Only fetches if the combo isn't cached yet.
        if (selectedFilter === 'custom') {
            if (customStartDate && customEndDate) {
                loadDashboard(customStartDate, customEndDate, filterType);
            }
        } else {
            const { startDate, endDate } = getDateRange(selectedFilter);
            loadDashboard(startDate, endDate, filterType);
        }
    }, [accessToken, selectedFilter, customStartDate, customEndDate, filterType]);

    // One-time background prefetch on mount — warms the cache for all common combos
    useEffect(() => {
        if (!accessToken) return;
        const { startDate, endDate } = getDateRange(selectedFilter);
        const primaryKey = `ar_dashboard_${startDate}_${endDate}_${filterType}`;
        prefetchCommonCombos(primaryKey);
    }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFilterChange = (value) => {
        setSelectedFilter(value);
        setIsFilterOpen(false);
    };

    const handleFilterTypeChange = (value) => {
        setFilterType(value);
        setIsFilterTypeOpen(false);
    };

    const handleCustomDateApply = () => {
        if (customStartDate && customEndDate) {
            loadDashboard(customStartDate, customEndDate, filterType);
        }
    };


    // --- Conditional Rendering ---
    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-zinc-900 text-zinc-400">
            <div className="text-center">
                <div className="w-8 h-8 mx-auto mb-4 border-b-2 border-blue-500 rounded-full animate-spin"></div>
                <p className="animate-pulse">Syncing with QuickBooks...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="flex items-center justify-center min-h-screen p-8 text-red-400 bg-zinc-900">
            <div className="max-w-md p-6 border rounded-lg border-red-900/50 bg-red-900/10">
                <h2 className="mb-2 text-xl font-bold">Connection Error</h2>
                <p className="text-sm">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 mt-4 text-xs transition border border-red-700 rounded bg-red-900/40 hover:bg-red-800/50"
                >
                    Retry Sync
                </button>
            </div>
        </div>
    );

    if (!data || !data.metrics) {
        return <div className="p-8 text-center text-white bg-zinc-900">No dashboard data found.</div>;
    }

    const { metrics, agingAnalysis, customers } = data;

    const agingEntries = Object.entries(agingAnalysis).map(([label, data]) => {
        // Handle both old (number) and new (object) format for backward compatibility if needed, 
        // though we prefer new format: { amount: 123, avgTrustScore: 88 }
        const amount = typeof data === 'number' ? data : data.amount;
        const score = typeof data === 'number' ? null : data.avgTrustScore;

        return {
            label,
            amount,
            trustScore: score,
            percentage: metrics.totalAR > 0 ? ((amount / metrics.totalAR) * 100).toFixed(1) : 0
        };
    });

    const displayMetrics = [
        { title: "TOTAL AR OUTSTANDING", value: `$${(metrics.totalAR || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, change: "Live Balance", isPositiveChange: true },
        { title: "COLLECTION EFFICIENCY", value: `${(metrics.collectionEfficiency || 0).toFixed(1)}%`, change: "Current Period", isPositiveChange: metrics.collectionEfficiency > 80 },
        { title: "CASH REALIZATION", value: `${(metrics.cashRealization || 0).toFixed(1)}%`, change: "vs. Forecast", isPositiveChange: metrics.cashRealization > 40 },
        { title: "AVG TRUST SCORE", value: `${(metrics.avgTrustScore || 0).toFixed(1)}`, change: "Customer Health", isPositiveChange: metrics.avgTrustScore > 80 },
        { title: "DAYS SALES OUTSTANDING", value: `${(metrics.daysSalesOutstanding || 0).toFixed(1)}d`, change: "Target: <30", isPositiveChange: metrics.daysSalesOutstanding < 20 },
        { title: "AT-RISK ACCOUNTS", value: metrics.atRiskAccounts, change: "Manual Review", isPositiveChange: metrics.atRiskAccounts === 0 },
        { title: "EBITDA", value: `$${(metrics.ebitda?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, change: "From P&L", isPositiveChange: (metrics.ebitda?.total || 0) > 0 },
    ];

    return (
        <div className="relative min-h-[calc(100vh-100px)] overflow-y-auto mt-8 text-zinc-100 bg-zinc-900">
            {/* Background Refresh Loading Bar */}
            {isRefreshing && (
                <div className="absolute top-0 left-0 right-0 z-50 h-1 bg-zinc-800">
                    <div className="h-full bg-blue-500 animate-loading-bar"></div>
                </div>
            )}

            <style jsx>{`
                @keyframes loading-bar {
                    0% { width: 0%; transform: translateX(0); }
                    50% { width: 100%; transform: translateX(0); }
                    100% { width: 100%; transform: translateX(100%); }
                }
                .animate-loading-bar {
                    animation: loading-bar 1.5s infinite linear;
                }
            `}</style>

            <div className="p-3">
                {/* Filter Bar */}
                <div className="flex flex-wrap items-center justify-end gap-2 mb-4">

                    {/* Time Range Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-zinc-800 border border-zinc-700 rounded-lg hover:bg-zinc-700 transition-all"
                        >
                            <span>{TIME_FILTER_OPTIONS.find(o => o.value === selectedFilter)?.label || "Select Range"}</span>
                            <svg className={`w-3 h-3 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {isFilterOpen && (
                            <div className="absolute right-0 z-20 mt-1 overflow-hidden border rounded-lg shadow-xl bg-zinc-800 border-zinc-700 min-w-[140px]">
                                {TIME_FILTER_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        onClick={() => handleFilterChange(option.value)}
                                        className={`w-full px-3 py-2 text-xs text-left hover:bg-zinc-700 transition-colors ${selectedFilter === option.value ? 'bg-zinc-700 text-blue-400' : 'text-zinc-300'}`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Filter Type Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setIsFilterTypeOpen(!isFilterTypeOpen)}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-zinc-800 border border-zinc-700 rounded-lg hover:bg-zinc-700 transition-all"
                        >
                            <span>{FILTER_TYPE_OPTIONS.find(o => o.value === filterType)?.label || "By Created"}</span>
                            <svg className={`w-3 h-3 transition-transform ${isFilterTypeOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {isFilterTypeOpen && (
                            <div className="absolute right-0 z-20 mt-1 overflow-hidden border rounded-lg shadow-xl bg-zinc-800 border-zinc-700 min-w-[140px]">
                                {FILTER_TYPE_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        onClick={() => handleFilterTypeChange(option.value)}
                                        className={`w-full px-3 py-2 text-xs text-left hover:bg-zinc-700 transition-colors ${filterType === option.value ? 'bg-zinc-700 text-blue-400' : 'text-zinc-300'}`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Custom Date Pickers */}
                    {selectedFilter === "custom" && (
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:border-blue-500"
                            />
                            <span className="text-xs text-zinc-500">to</span>
                            <input
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:border-blue-500"
                            />
                            <button
                                onClick={handleCustomDateApply}
                                disabled={!customStartDate || !customEndDate}
                                className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                Apply
                            </button>
                        </div>
                    )}

                    {/* Divider */}
                    <div className="w-px h-4 bg-zinc-700" />

                    {/* View Report Button */}
                    <button
                        onClick={() => setShowReport(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-zinc-800 border border-zinc-600 rounded-lg hover:bg-zinc-700 hover:border-zinc-500 transition-all text-zinc-300"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        View Report
                    </button>

                    {/* Sync Button */}
                    <button
                        onClick={handleSync}
                        disabled={isRefreshing}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 border border-blue-500 rounded-lg hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                    >
                        <svg className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {isRefreshing ? 'Syncing...' : 'Sync'}
                    </button>
                </div>

                {/* 1. Key Metric Cards */}
                <div className="grid grid-cols-2 gap-2 mb-3 md:grid-cols-3 lg:grid-cols-6">
                    {displayMetrics.map((metric, index) => (
                        <div key={index} className="flex-auto p-2 border rounded-lg shadow-lg bg-zinc-800 border-zinc-700">
                            <h3 className="mb-1 text-[10px] font-medium uppercase truncate text-zinc-400">{metric.title}</h3>
                            <div className="flex flex-col flex-shrink">
                                <div className="text-lg font-extrabold text-white md:text-xl">{metric.value}</div>
                                <div className={`mt-0.5 text-[10px] font-semibold truncate ${metric.isPositiveChange ? "text-green-500" : "text-yellow-500"}`}>
                                    {metric.change}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* 2. AR Aging Analysis and Customer Overview */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <div className="p-4 border rounded-lg shadow-sm lg:col-span-1 bg-zinc-800 border-zinc-700">
                        <h2 className="pb-2 mb-4 text-sm font-semibold border-b text-zinc-100 border-zinc-700">AR Aging Analysis</h2>
                        <div className="space-y-3">
                            {agingEntries.map((item, index) => (
                                <div key={index}>
                                    <div className="flex items-center justify-between text-xs mb-0.5">
                                        <div className="flex items-center gap-2">
                                            <span className={`font-medium ${item.amount > 0 ? "text-zinc-300" : "text-zinc-600"}`}>{item.label}</span>
                                            {item.trustScore !== null && item.amount > 0 && (
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${getTrustScoreStyles(item.trustScore)}`}>
                                                    TS: {Number(item.trustScore).toFixed(1)}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-right text-[10px] sm:text-xs">
                                            <span className={`mr-1 font-semibold ${item.amount > 0 ? "text-white" : "text-zinc-600"}`}>${item.amount.toLocaleString()}</span>
                                            <span className="text-zinc-400">({item.percentage}%)</span>
                                        </span>
                                    </div>
                                    <div className="h-1.5 rounded-full bg-zinc-700">
                                        <div
                                            className={`h-1.5 rounded-full transition-all duration-500 ${getProgressBarColor(item.label)}`}
                                            style={{ width: `${item.percentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-col p-0 border rounded-lg shadow-sm lg:col-span-2 bg-zinc-800 border-zinc-700">
                        <h2 className="px-4 pt-3 pb-2 text-sm font-semibold border-b text-zinc-100 border-zinc-700">Customer AR Overview</h2>
                        <div className="overflow-x-auto max-h-[400px]">
                            <table className="w-full text-xs table-auto">
                                <thead className="sticky top-0 z-10">
                                    <tr className="text-left uppercase text-zinc-400 bg-zinc-800/95">
                                        {CUSTOMER_COLUMNS.map((col) => (
                                            <th
                                                key={col.key}
                                                onClick={() => handleSort(col.key)}
                                                className={`px-4 py-2 font-semibold cursor-pointer hover:text-zinc-200 transition-colors select-none ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
                                                    }`}
                                            >
                                                <span className="inline-flex items-center">
                                                    {col.label}
                                                    {getSortIndicator(col.key)}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-700/50">
                                    {getSortedCustomers(customers).map((customer, idx) => (
                                        <tr key={customer.id || idx} className={`transition duration-150 hover:bg-zinc-700/40 ${customer.isAtRisk ? 'bg-red-900/10' : ''}`}>
                                            <td className="py-2 px-4 font-medium text-blue-300 truncate max-w-[150px]">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    {customer.isAtRisk && <IoIosWarning className="text-red-500 flex-shrink-0" title="At Risk (>60 days overdue)" />}
                                                    <span className="truncate">{customer.customer}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 font-mono text-right text-zinc-200">${(customer.outstanding || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            <td className="px-4 py-2 text-center">
                                                <span className={`inline-flex items-center justify-center text-[10px] font-bold rounded px-2 py-0.5 ${getTrustScoreStyles(customer.trustScore)}`}>
                                                    {(customer.trustScore || 0).toFixed(1)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-center text-zinc-300">
                                                {customer.terms || <span className="italic text-zinc-600">—</span>}
                                            </td>
                                            <td className="px-4 py-2 text-right text-zinc-300">
                                                {customer.dueDate
                                                    ? new Date(customer.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
                                                    : <span className="italic text-zinc-600">—</span>}
                                            </td>
                                            <td className="px-4 py-2 text-center">
                                                {customer.status && customer.status !== "Unknown" ? (
                                                    <div className="inline-flex items-center gap-1">
                                                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded border ${customer.status === "Overdue" ? "bg-red-900/30 text-red-400 border-red-900/50" : "bg-green-900/30 text-green-400 border-green-900/50"}`}>
                                                            {customer.status}
                                                        </span>
                                                        {customer.status === "Overdue" && customer.overdueCount > 1 && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold rounded bg-red-500/20 text-red-400 border border-red-500/40" title={`${customer.overdueCount} overdue invoices`}>
                                                                +{customer.overdueCount - 1}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="italic text-zinc-600 text-[10px]">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right text-zinc-400">
                                                {customer.lastInvoiceDate || <span className="italic text-zinc-600">No data</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* AR Report Document Overlay */}
            {showReport && (
                <ArReportDocument
                    data={data}
                    period={{
                        startDate: selectedFilter === 'custom' ? customStartDate : getDateRange(selectedFilter).startDate,
                        endDate: selectedFilter === 'custom' ? customEndDate : getDateRange(selectedFilter).endDate,
                        filterType,
                    }}
                    onClose={() => setShowReport(false)}
                />
            )}
        </div>
    );
};

export default AripDashboard;